import * as React from "react";
import { Button, Form, Input, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { LOGIN_SUCCESS } from "../../redux/auth/authType";
import { responseNotification } from "../../utils/notify";
import BreadCrumb from "../Layouts/Breadcrumb";
import { debounce } from "lodash";

export const BusinessCreateForm: React.FC = () => {
  const { token, shopId: reduxShopId } = useSelector(
    (state: any) => state.authReducer
  );
  const fetchRef = useRef<any>(0);
  const [operationType, setOperationType] = useState("INDIVIDUAL");
  const [businessTypeId, setBusinessTypeId] = useState<any>(null);
  const [serviceId, setServiceId] = useState<any>(null);
  const [businessCatId, setBusinessCatId] = useState<any>("");
  const [businessCusiginId, setBusinessCusiginId] = useState<string | undefined>(undefined);
  const [businessCatOptions, setBusinessCatOptions] = useState<any>({
    list: [],
    loading: false,
  });
  const [businessCusigineOptions, setBusinessCusigineOptions] = useState<any>({
    list: [],
    loading: false,
  });
  const [merchantDetails, setMerchantDetails] = useState<any>(null);
  const [businessCategoryId, setBusinessCategoryId] = useState("");
  const [businessCuisineId, setBusinessCuisineId] = useState("");

  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [businessTypeOptions, setBusinessTypeOptions] = useState<any>({
    list: [],
  });
  const [serviceOptions, setServiceOptions] = useState<any>({ list: [] });
  const existingShopId = useMemo(() => {
    return (
      reduxShopId ||
      merchantDetails?.shopId ||
      merchantDetails?.shop?.id ||
      merchantDetails?.storeDetails?.shopId
    );
  }, [reduxShopId, merchantDetails]);

  const isMerchantExists = Boolean(existingShopId);
  const loadBusinessTypes = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_CATALOG_WRITER_API}/shop/service-type-list?page=0&limit=20`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setBusinessTypeOptions({
        list: (res.data?.businessTypes || []).map((b: any) => ({
          value: b.id,
          label: b.name,
        })),
      });
    } catch (e) {
      responseNotification("Failed to load Business Types", "error");
    }
  }, [token]);
  const loadServiceTypes = useCallback(async () => {
    if (!businessTypeId) {
      setServiceOptions({ list: [] });
      return;
    }

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_CATALOG_WRITER_API}/shop/details-business-type?id=${businessTypeId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setServiceOptions({
        list: (res.data?.businessType?.serviceTypes || []).map((s: any) => ({
          value: s.value,
          label: s.name,
        })),
      });
    } catch (e) {
      responseNotification("Failed to load Service Types", "error");
    }
  }, [businessTypeId, token]);
  const fetchMerchantDetails = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_RIDER_API}/details`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.status === 200) {
        const md = res.data?.merchant;
        setMerchantDetails(md);

        setOperationType(md?.businessOperationType || "INDIVIDUAL");
        setBusinessTypeId(md?.businessTypeId || null);
        setServiceId(md?.serviceType || null);

        form.setFieldsValue({
          businessOperationType: md?.businessOperationType,
          businessTypeId: md?.businessTypeId,
          serviceTypeId: md?.serviceType,
          name: md?.shopName,
          companyName: md?.companyName,
        });
      }
    } catch (e) {
      responseNotification("Failed to fetch merchant details", "warning");
    }
  }, [token, form]); 
  useEffect(() => {
    loadBusinessTypes();
    fetchMerchantDetails();
  }, [loadBusinessTypes, fetchMerchantDetails]);

  useEffect(() => {
    loadServiceTypes();
  }, [loadServiceTypes]);
  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      if (isMerchantExists) {
        const updatePayload = {
          id: existingShopId,
          type: data.serviceTypeId,
          name: data.name,
        };

        const res = await fetch(
          `${process.env.REACT_APP_CATALOG_WRITER_API}/shop`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(updatePayload),
          }
        );

        const json = await res.json();

        if (json?.statusCode === 200) {
          responseNotification("Shop Updated Successfully", "success");
          navigate("/merchant-profile");
        } else {
          responseNotification(json?.message || "Update failed", "error");
        }
        return;
      }
      await fetch(
        `${process.env.REACT_APP_RIDER_API}/merchant/choose-business-model`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            businessOperationType: operationType,
            businessTypeId: businessTypeId,
            companyName: data.companyName,
          }),
        }
      );

      const createRes = await fetch(
        `${process.env.REACT_APP_CATALOG_WRITER_API}/shop/create-shop-by-sm`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            type: data.serviceTypeId,
            businessOperationType: operationType,
            businessCategoryId: businessCatId,
            businessSubCategoryId: businessCusiginId,
            name: data.name,
            isActive: false,
          }),
        }
      );

      const createJson = await createRes.json();

      if (createJson?.statusCode === 200) {
        dispatch({
          type: LOGIN_SUCCESS,
          payload: {
            ...createJson,
            token,
            shopInfo: createJson.shop,
            shopId: createJson.shop?.id,
          },
        });

        responseNotification("Business Created Successfully", "success");
        navigate("/merchant-profile");
      } else {
        responseNotification(
          createJson?.message || "something wrong",
          "warning"
        );
      }
    } catch (err) {
      responseNotification("Internal Server Error", "error");
    } finally {
      setLoading(false);
    }
  };
  const getCountryOptions = useCallback(async () => {
    setBusinessCatOptions({ loading: true, list: [] });
    const res = await axios.get(
      `${process.env.REACT_APP_CATALOG_READER_API}/business-category?type=${serviceId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    setBusinessCatOptions({
      loading: false,
      list: res?.data?.businessCategories?.map((category: any) => {
        return {
          value: category?.id,
          label: category?.name,
        };
      }),
    });
  }, [serviceId]);
  const getStateOptions = useCallback(
    async (key?: string) => {
      setBusinessCusigineOptions({ loading: true, list: [] });

      const res = await axios.get(
        `${process.env.REACT_APP_CATALOG_READER_API}/business-sub-category?type=${serviceId}&businessCategoryId=${businessCatId}` +
          (key ? `&key=${key}` : ``),

        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setBusinessCusigineOptions({
        loading: false,
        list: res?.data?.businessSubCategories?.map((subCat: any) => {
          return {
            value: subCat?.id,
            label: subCat?.name,
          };
        }),
      });
    },
    [serviceId, businessCatId]
  );
  useEffect(() => {
    getCountryOptions();
    getStateOptions();
  }, [getCountryOptions, getStateOptions]);

  const handleSearch = useMemo<any>(() => {
    const loadOptions = (value: string, field: string) => {
      fetchRef!.current += 1;
      const fetchId = fetchRef.current;

      if (fetchId !== fetchRef.current) {
        return;
      }

      if (value) {
        if (field === "country") getCountryOptions(value);
      }
    };

    return debounce(loadOptions, 800);
  }, [getCountryOptions]);

  return (
    <div className="flex overflow-hidden flex-col bg-stone-50 pb-[10px]">
      <main className="flex flex-col bg-white p-4 self-center mt-5 w-[50%] rounded-2xl">
        <BreadCrumb
          title={isMerchantExists ? "Update Business" : "Create Business"}
          className="flex gap-2 bg-red-700 text-base font-bold text-zinc-800"
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          size="large"
          className="equal-height-form mt-6"
        >
          <Form.Item
            label="Business Operation Type"
            name="businessOperationType"
            rules={[
              {
                required: true,
                message: "Business Operation Type is required!",
              },
            ]}
          >
            <Select
              placeholder="Select Business Operation Type"
              value={operationType}
              onChange={(val) => {
                setOperationType(val);
                form.setFieldsValue({ businessOperationType: val });
              }}
              options={[
                { value: "INDIVIDUAL", label: "None, I am an Individual" },
                { value: "COMPANY", label: "As a Company" },
              ]}
            />
          </Form.Item>

          <Form.Item
            label="Business Type"
            name="businessTypeId"
            rules={[{ required: true, message: "Business Type is required!" }]}
          >
            <Select
              placeholder="Select Business Type"
              value={businessTypeId}
              onChange={(val) => {
                setBusinessTypeId(val);
                setServiceId(null);
                form.setFieldsValue({
                  businessTypeId: val,
                  serviceTypeId: null,
                });
              }}
              options={businessTypeOptions.list}
            />
          </Form.Item>

          <Form.Item
            label="Service Type"
            name="serviceTypeId"
            rules={[{ required: true, message: "Service Type is required!" }]}
          >
            <Select
              placeholder="Select Service Type"
              value={serviceId}
              onChange={(val) => {
                setServiceId(val);
                form.setFieldsValue({ serviceTypeId: val });
              }}
              options={serviceOptions.list}
              disabled={!businessTypeId}
            />
          </Form.Item>
          {serviceId === "JC_FOOD" && (
            <>
              <Form.Item
                hasFeedback
                label="Business Category"
                rules={[
                  {
                    required: true,
                    message: "Business Category is Required!",
                  },
                ]}
                name="country"
              >
                <Select
                  allowClear
                  showSearch
                  placeholder="Select Business Category"
                  value={businessCatId}
                  onChange={(val) => {
                    setBusinessCatId(val);
                    setBusinessCusiginId(undefined);
                    form.setFieldsValue({ businessCusigin: undefined });
                  }}
                  options={businessCatOptions?.list}
                />
              </Form.Item>
              <Form.Item
                hasFeedback
                label="Business Cuisine"
                rules={[
                  {
                    required: true,
                    message: "Business Cuisine is Required!",
                  },
                ]}
                name="businessCusigin"
              >
                <Select
                  allowClear
                  showSearch
                  placeholder="Select Cuisine"
                  optionFilterProp="children"
                  onChange={(val) => setBusinessCusiginId(val)}
                  onSearch={(val) => {
                    handleSearch?.(val, "cuisine");
                  }}
                  filterOption={() => {
                    return true;
                  }}
                  options={businessCusigineOptions?.list}
                ></Select>
              </Form.Item>
            </>
          )}
          <Form.Item
            label="Business Name"
            name="name"
            rules={[{ required: true, message: "Business Name is required!" }]}
          >
            <Input placeholder="Enter Business name" />
          </Form.Item>

          <Button loading={loading} type="primary" htmlType="submit">
            {isMerchantExists ? "Update" : "Submit"}
          </Button>
        </Form>
      </main>
    </div>
  );
};

export default BusinessCreateForm;
